import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { FiHome, FiLogOut, FiChevronRight, FiChevronLeft, FiMenu, FiX, FiSun, FiMoon, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import SidebarNavItem from './components/sidebar/SidebarNavItem';
import SidebarLink from './components/sidebar/SidebarLink';
import { navSections } from './components/sidebar/navConfig';
import { resolveActiveLink } from './components/sidebar/activeRoute';
import Breadcrumbs from './components/sidebar/Breadcrumbs';

function SidebarContent({ open, setOpen }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sidebarRef = useRef();

  // One source of truth for "which link is active", so a section index and its
  // children can't both light up.
  const activeLink = resolveActiveLink(pathname, navSections);
  const activeSectionKey = activeLink?.sectionKey ?? null;

  // Open the section that owns the current route, otherwise the highlight is
  // invisible on a deep link or refresh (every section starts collapsed).
  const [openSection, setOpenSection] = useState(activeSectionKey);
  const [collapsed, setCollapsed] = useState(false);

  // Re-open only when navigation moves to a *different* section, so collapsing
  // the current one by hand isn't immediately undone.
  const autoOpenedRef = useRef(activeSectionKey);
  useEffect(() => {
    if (activeSectionKey && activeSectionKey !== autoOpenedRef.current) {
      autoOpenedRef.current = activeSectionKey;
      setOpenSection(activeSectionKey);
    }
  }, [activeSectionKey]);

  const toggleSection = (section) =>
    setOpenSection(openSection === section ? null : section);

  // Clear auth state AND navigate, so logout works even from public routes
  // like /blackbook (which won't auto-redirect on auth change).
  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Close on outside click (mobile)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target) && open) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  if (!user) {
    return (
      <aside className="fixed md:sticky top-0 left-0 h-screen w-64 flex items-center justify-center bg-bg-raised border-r border-border">
        <p className="text-text-muted text-sm">Loading...</p>
      </aside>
    );
  }

  const roleId = Number(user.role_id);
  const sidebarWidth = collapsed ? 'w-16' : 'w-64';

  return (
    <>
      {/* Mobile hamburger toggle */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed left-2 top-2 z-50 p-2.5 rounded-lg bg-bg-raised/90 backdrop-blur-sm border border-border text-text-secondary hover:text-text hover:bg-bg-elevated transition-colors md:hidden touch-target"
          aria-label="Open navigation"
          style={{ paddingTop: `max(0.625rem, env(safe-area-inset-top, 0.625rem))` }}
        >
          <FiMenu size={22} />
        </button>
      )}

      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          fixed md:sticky top-0 left-0 h-screen z-50
          flex flex-col
          bg-bg-glass backdrop-blur-md backdrop-saturate-150
          border-r border-mix/[0.06]
          transform transition-all duration-200 ease-smooth
          ${sidebarWidth}
          ${open ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-mix/[0.06] flex-shrink-0">
          <div
            className="flex items-center gap-2.5 cursor-pointer group"
            onClick={() => navigate('/dashboard')}
          >
            <span className="relative inline-flex w-8 h-8 items-center justify-center rounded-lg bg-primary-gradient shadow-glow-sm">
              <FiHome className="w-4 h-4 text-white" />
            </span>
            {!collapsed && (
              <span className="text-[15px] font-semibold text-text tracking-tight">
                Ruby <span className="gradient-text">Form</span>
              </span>
            )}
          </div>

          {/* Desktop collapse toggle */}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden md:flex p-1 rounded text-text-muted hover:text-text hover:bg-bg-elevated transition-colors"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
          </button>

          {/* Mobile close */}
          <button
            onClick={() => setOpen(false)}
            className="p-1 rounded text-text-muted hover:text-text md:hidden"
            aria-label="Close navigation"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto overscroll-contain">
          {navSections
            .filter((s) => roleId <= s.maxRole)
            .map((section) => (
              <SidebarNavItem
                key={section.key}
                icon={section.icon}
                label={section.label}
                collapsed={collapsed}
                isOpen={openSection === section.key}
                onToggle={() => toggleSection(section.key)}
              >
                {section.links.map((link) => (
                  <SidebarLink
                    key={link.to}
                    to={link.to}
                    label={link.label}
                    isActive={link.to === activeLink?.to}
                  />
                ))}
              </SidebarNavItem>
            ))}
        </nav>

        {/* Logout */}
        <div className="p-2 border-t border-border flex-shrink-0 safe-bottom">
          <button
            onClick={handleLogout}
            className={`
              flex items-center gap-2 w-full rounded-md
              text-sm font-medium text-danger
              hover:bg-danger-muted transition-colors min-h-[44px]
              ${collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}
            `}
          >
            <FiLogOut size={16} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>
    </>
  );
}

// Global back / forward history controls. Every route renders through
// SidebarLayout, so these give app-wide navigation (e.g. horse-stats ->
// horse detail -> back to horse-stats). We track React Router's history
// index (stored on window.history.state.idx) so the buttons can correctly
// enable/disable rather than firing navigate(-1) into a dead end.
function NavControls() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'POP' | 'REPLACE'
  const [idx, setIdx] = useState(() => window.history.state?.idx ?? 0);
  const [maxIdx, setMaxIdx] = useState(() => window.history.state?.idx ?? 0);

  useEffect(() => {
    const current = window.history.state?.idx ?? 0;
    setIdx(current);
    // A PUSH creates a new entry and truncates any forward history; a POP
    // (back/forward) keeps the furthest point we've reached reachable.
    setMaxIdx((prev) => (navType === 'PUSH' ? current : Math.max(prev, current)));
  }, [location.key, navType]);

  const canBack = idx > 0;
  const canForward = idx < maxIdx;

  const btn = (enabled) =>
    `p-2 rounded-lg transition-colors ${
      enabled
        ? 'text-text-muted hover:text-text hover:bg-mix/[0.06]'
        : 'text-text-muted/30 cursor-not-allowed'
    }`;

  return (
    <div className="flex items-center">
      <button
        onClick={() => canBack && navigate(-1)}
        disabled={!canBack}
        className={btn(canBack)}
        aria-label="Go back"
        title="Back"
      >
        <FiArrowLeft size={18} />
      </button>
      <button
        onClick={() => canForward && navigate(1)}
        disabled={!canForward}
        className={btn(canForward)}
        aria-label="Go forward"
        title="Forward"
      >
        <FiArrowRight size={18} />
      </button>
    </div>
  );
}

function ThemeToggle() {
  const { isDark, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg text-text-muted hover:text-text hover:bg-mix/[0.06] transition-colors"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      {isDark ? <FiSun size={18} /> : <FiMoon size={18} />}
    </button>
  );
}

export function SidebarLayout({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close mobile sidebar on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-bg">
      <SidebarContent open={open} setOpen={setOpen} />
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar: back/forward + breadcrumbs on the left, theme on the right */}
        <header className="hidden md:flex items-center gap-2 h-10 px-4 flex-shrink-0">
          <NavControls />
          <div className="w-px h-5 bg-mix/[0.1]" />
          <Breadcrumbs />
          <ThemeToggle />
        </header>
        {/* Mobile: float the controls top-right */}
        <div className="fixed top-2 right-2 z-40 flex items-center gap-0.5 rounded-lg bg-bg-raised/90 backdrop-blur-sm border border-border md:hidden">
          <NavControls />
          <ThemeToggle />
        </div>
        {/* Mobile breadcrumb strip (leave room for the floating controls) */}
        <div className="md:hidden px-3 pt-11 pb-1 pr-28">
          <Breadcrumbs />
        </div>
        <main className="flex-1 min-w-0 pt-1 md:pt-0">
          {children}
        </main>
      </div>
    </div>
  );
}

export default SidebarContent;
